import { useEffect, useState } from "react";
import { Inbox, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, apiError } from "@/lib/api";

export function BandejaPendientes() {
  const [data, setData] = useState(null);
  const [cobradores, setCobradores] = useState([]);
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState("");

  const load = () => api.get("/cartera/pendientes").then((r) => setData(r.data)).catch(() => setData({ total: 0, clientes: [] }));
  useEffect(() => {
    load();
    api.get("/usuarios/cobradores").then((r) => setCobradores(r.data || [])).catch(() => {});
  }, []);

  const asignar = async (cid) => {
    const cobradorId = sel[cid];
    if (!cobradorId) { toast.error("Selecciona un responsable de cartera."); return; }
    setBusy(cid);
    try {
      await api.post(`/clientes/${cid}/reasignar`, { cobrador_id: cobradorId, motivo: "Asignación desde la bandeja de pendientes." });
      toast.success("Cliente asignado a su responsable de cartera.");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const asignarAOriginador = async () => {
    setBusy("__bloque__");
    try {
      const { data } = await api.post("/cartera/pendientes/asignar-a-originador");
      toast.success(`Asignados a quien los capturó: ${data.asignados}. Sin asesora asignable: ${data.omitidos}.`);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  if (!data || !data.total) return null;
  return (
    <div data-testid="bandeja-pendientes" className="rounded-lg border border-amber-200 bg-amber-50/60 p-4">
      <div className="mb-3 flex items-center gap-2">
        <Inbox className="h-4 w-4 text-amber-600" />
        <p className="text-sm font-semibold text-slate-800">Pendientes por asignar</p>
        <span className="rounded-full bg-amber-200 px-2 py-0.5 text-xs font-medium text-amber-800" data-testid="bandeja-count">{data.total}</span>
        {data.clientes.some((c) => c.originador_asignable) && (
          <Button size="sm" variant="outline" className="ml-auto" disabled={busy === "__bloque__"}
            onClick={asignarAOriginador} data-testid="bandeja-asignar-originador">
            {busy === "__bloque__" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Asignar a quien capturó"}
          </Button>
        )}
      </div>
      <div className="space-y-2">
        {data.clientes.map((c) => (
          <div key={c.id} className="flex flex-wrap items-center gap-2 rounded-md border border-amber-200 bg-white px-3 py-2" data-testid={`pendiente-${c.id}`}>
            <div className="min-w-[160px] flex-1">
              <p className="text-sm font-medium text-slate-800">{c.nombre || c.razon_social}</p>
              <p className="text-[11px] text-slate-400">
                {c.rfc || "—"} · Originó: {c.originador_nombre} ·{" "}
                <span className={c.tiene_credito ? "text-emerald-700" : "text-slate-500"}>{c.tiene_credito ? "Con crédito" : "Sin crédito"}</span>
              </p>
            </div>
            <Select value={sel[c.id] || ""} onValueChange={(v) => setSel((s) => ({ ...s, [c.id]: v }))}>
              <SelectTrigger className="h-8 w-52" data-testid={`pendiente-select-${c.id}`}><SelectValue placeholder="Asignar a..." /></SelectTrigger>
              <SelectContent>{cobradores.map((co) => <SelectItem key={co.id} value={co.id}>{co.name}</SelectItem>)}</SelectContent>
            </Select>
            <Button size="sm" disabled={busy === c.id} onClick={() => asignar(c.id)} data-testid={`pendiente-asignar-${c.id}`} className="bg-accent hover:bg-forest-700">
              {busy === c.id ? <Loader2 className="h-4 w-4 animate-spin" /> : "Asignar"}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
